/* Makes the Pac-Man on the values band actually eat.

   CSS moves both parties — the marks drift left, he runs right — but it cannot
   say when they meet. The two animations have different periods and no fixed
   phase relationship, so the moment of any given collision is not a constant
   the keyframes could encode. This watches instead: one frame loop, comparing
   his jaw against each mark's box.

   Three details worth keeping:

   1. A mark is bitten when it reaches the back of his mouth, not the front of
      his box. The mouth is a wedge cut in to just short of his middle, so with
      it open the front of the box is empty ground: biting there made a mark
      vanish half its own width before he touched it, which on a phone is most
      of the way across the gap.

   2. Marks grow back. He crosses the band every few seconds while a mark takes
      the best part of half a minute to drift across it, so a mark eaten for
      good is a mark missing for its whole visit — measured on the first build,
      that left most of the strip as bare text. A mark now returns once he is
      well past it, roughly a second later, which reads as him clearing a path
      rather than stripping the band.

   3. It has to be a distance, not a timer. Both parties are moving, so the gap
      between them is the only thing that says he has finished with that mark
      and is not about to be dragged back over it.

   Nothing here runs for a visitor who asked for reduced motion — the band drops
   its marquee and its Pac-Man in CSS, and there is then nothing to watch. */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/* Where the bite lands, as a fraction of his width from his back edge: the
   point of the mouth wedge is drawn at 44%, so a mark whose leading edge has
   passed this line is already inside his mouth and partly behind his head. */
const BITE_LINE = 0.5;

/* How far behind him a mark has to fall before it grows back, in pixels. He
   and the track separate at roughly eighty pixels a second at the shipped
   speeds, so this is a second and a half of absence. */
const REVIVE_GAP = 130;

function setUp(band) {
  const pac = band.querySelector('[data-values-pac]');
  const track = band.querySelector('.bwl-values__track');
  const template = track && track.firstElementChild;
  if (!pac || !template) return;

  let marks = [];

  /* The loop only reads as endless while one half of the track is at least as
     wide as the band, because the track restarts by sliding exactly that half
     out of view. Two passes cover a phone several times over; on a desktop one
     pass is around a thousand pixels against a fourteen-hundred-pixel band, so
     the far edge of the second half arrives before the first has finished
     leaving and the strip runs out into white.

     How many passes that takes is a fact about the viewport, not about the
     content, so Liquid cannot know it and it is settled here instead. Always an
     even number: the half is what the -50% keyframe slides, so the two halves
     have to be identical for the seam to stay invisible. */
  const fill = () => {
    const passWidth = template.getBoundingClientRect().width;

    if (passWidth) {
      const half = Math.max(1, Math.ceil(band.clientWidth / passWidth));
      const wanted = half * 2;

      while (track.children.length < wanted) {
        const copy = template.cloneNode(true);
        /* Only the authored first pass is read out; every copy is scenery. */
        copy.setAttribute('aria-hidden', 'true');
        /*
          A fresh pass is always whole. cloneNode copies class names, and this
          runs again when the fonts land — a second or so in, by which point he
          has usually taken the first mark. Without this the copy is born with
          that mark already eaten, and it cannot heal: a mark grows back once he
          is past it, and these sit ahead of him, so the hole rides the band
          until it drifts off the far edge. Three copies on a desktop against
          one on a phone, which is why it only ever looked broken on the wide
          screen.
        */
        copy.querySelectorAll('[data-values-icon]').forEach((icon) => {
          icon.classList.remove('is-eaten');
        });
        track.appendChild(copy);
      }

      while (track.children.length > wanted) track.lastElementChild.remove();
    }

    marks = [...track.querySelectorAll('[data-values-icon]')];
  };

  /* His run has to cross the band, and only the layout knows how wide that is. */
  const measure = () => {
    fill();
    band.style.setProperty('--bwl-pac-travel', `${band.clientWidth + pac.offsetWidth}px`);
    band.style.setProperty('--bwl-pac-size', `${pac.offsetWidth}px`);
  };
  measure();
  if (marks.length === 0) return;
  new ResizeObserver(measure).observe(band);

  /* A pass is mostly text, so its width is not final until the real faces have
     swapped in. The band itself barely changes height when they do, which is not
     enough to be sure the observer above will fire. */
  if (document.fonts) document.fonts.ready.then(measure);

  let frame = 0;

  const tick = () => {
    frame = requestAnimationFrame(tick);
    const bandBox = band.getBoundingClientRect();
    const pacBox = pac.getBoundingClientRect();
    const biteLine = pacBox.left + pacBox.width * BITE_LINE;

    for (const mark of marks) {
      /*
        Measured from the item it sits in, not from the mark itself: an eaten
        mark is scaled down, which shrinks its own box towards the centre and
        would put its left edge ahead of him the moment it is bitten — it would
        grow straight back and be eaten again, flickering on every bite.
      */
      const left = mark.parentElement.getBoundingClientRect().left;
      const box = { left, right: left + mark.offsetWidth };
      const eaten = mark.classList.contains('is-eaten');

      if (eaten) {
        const clear = pacBox.left > box.right + REVIVE_GAP;
        const offBand = box.right < bandBox.left || box.left > bandBox.right;
        /*
          A mark can only be ahead of him once he has started a new run: he
          eats it near the right edge, jumps back to the left, and it is now in
          front of him again. Waiting for him to pass it a second time left it
          missing for most of a crossing — thirty seconds on a wide screen — so
          he ran over gaps instead of marks. It grows back as the run restarts.
        */
        const ahead = box.left > pacBox.right;
        if (clear || offBand || ahead) mark.classList.remove('is-eaten');
        continue;
      }

      /* Past the right edge he is still running but out of sight, and a mark
         there is off the band and would grow straight back: leave it alone. */
      const onBand = box.left < bandBox.right && box.right > bandBox.left;
      if (onBand && box.left < biteLine && box.right > pacBox.left) mark.classList.add('is-eaten');
    }
  };

  /* Off-screen the band is not animating and nothing can collide, so stop
     asking. This also keeps a background tab from holding a frame loop open. */
  const watching = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && !frame) tick();
      else if (!entry.isIntersecting && frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }
  });
  watching.observe(band);
}

function start() {
  if (window.matchMedia(REDUCED_MOTION).matches) return;
  document.querySelectorAll('[data-bwl-values]').forEach(setUp);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
